import { Fragment, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { observer } from "mobx-react-lite";
import AppFrame from "./AppFrame";
import { useStore } from "../stores/store";
import { Loading } from "@shopify/polaris";
import LoginPage from "../../features/users/LoginPage";
import LandingPage from "../../features/home/LandingPage";
import { hasShopifyAuthParams, stripShopifyAuthParams } from "../utils/shopifyAuth";
import { rememberLastAppRoute } from "../utils/lastAppRoute";
import ProductFitmentCopyActivity from "../../features/products/ProductFitmentCopyActivity";
import AppNavMenu from "./AppNavMenu";
import { isEmbeddedAdminFrame } from "../components/SafeAppBridge";
import { LegacyModeProvider } from "../../features/legacy/LegacyModeContext";
import LegacyModeBanner from "../../features/legacy/LegacyModeBanner";
import LegacyTopNav from "../../features/legacy/LegacyTopNav";
import PilotOperatorBanner from "../../features/legacy/PilotOperatorBanner";
import OperatorNavigation from "../../features/legacy/OperatorNavigation";
import SupportWidget from "../../features/support/SupportWidget";

const App = () => {
  const { commonStore, userStore } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionRestore = useRef<Promise<void> | null>(null);
  const initialSearch = useRef(location.search).current;
  const launchShop = new URLSearchParams(location.search).get("shop")?.trim().toLowerCase();
  const sessionShop = userStore.user?.shopDomain.trim().toLowerCase();
  const shopMismatch = Boolean(launchShop && sessionShop && launchShop !== sessionShop);

  useEffect(() => {
    let cancelled = false;
    // Restore a saved session once. URL cleanup and ordinary navigation must
    // not start additional current-user requests while restoration is pending.
    if (!sessionRestore.current) {
      const hadSession = Boolean(commonStore.token || commonStore.shopifySession);
      const params = new URLSearchParams(initialSearch);
      const oauthCallback = Boolean(params.get("shop") && params.get("code") && params.get("state"));
      if (oauthCallback) {
        // A callback must consume its signed code/state even if this browser
        // still holds a previous local session.
        userStore.logout();
      } else if (hasShopifyAuthParams(initialSearch) && !userStore.isLoggedIn) {
        commonStore.useShopifySession();
      }
      sessionRestore.current = !oauthCallback && hadSession && !userStore.isLoggedIn
        ? userStore.getUser()
        : Promise.resolve();
    }
    void sessionRestore.current.then(() => {
      if (!cancelled) commonStore.setAppLoaded();
    });
    return () => { cancelled = true; };
  }, [commonStore, initialSearch, userStore]);

  useEffect(() => {
    if (!commonStore.appLoaded || !userStore.isLoggedIn) return;
    // Keep launch parameters until the restored session belongs to this shop.
    // An expired or different-shop session still needs them to authenticate.
    if (shopMismatch) {
      userStore.logout();
      return;
    }
    // LoginPage owns the /login redirect, including its return destination.
    if (location.pathname !== "/login" && hasShopifyAuthParams(location.search)) {
      navigate(`${location.pathname}${stripShopifyAuthParams(location.search)}${location.hash}`, { replace: true });
    }
  }, [commonStore.appLoaded, location.hash, location.pathname, location.search, navigate, shopMismatch, userStore, userStore.isLoggedIn]);

  useEffect(() => {
    if (userStore.isLoggedIn) {
      rememberLastAppRoute(location.pathname, location.search, location.hash);
    }
  }, [location.hash, location.pathname, location.search, userStore.isLoggedIn]);

  const isShopifyLaunch = hasShopifyAuthParams(location.search);
  const showAppNav = isEmbeddedAdminFrame();

  if (!commonStore.appLoaded || shopMismatch) return (
    <Fragment>
      <AppFrame><div style={{ height: '100px' }}><Loading /></div></AppFrame>
    </Fragment>
  )

  if (!userStore.isLoggedIn) {
    if (location.pathname === '/login' || isShopifyLaunch || commonStore.shopifySession) {
      return (
        <Fragment>
          <LoginPage />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <LandingPage />
      </Fragment>
    );
  }

  return (
    <LegacyModeProvider>
      <Fragment>
        {showAppNav && <AppNavMenu />}
        <AppFrame>
          <div style={{ position: 'sticky', top: 0, zIndex: 510 }}><PilotOperatorBanner /></div>
          <div className="LegacyChrome">
            {!showAppNav && <OperatorNavigation />}
            <LegacyModeBanner />
            <LegacyTopNav />
          </div>
          <ProductFitmentCopyActivity />
          <Outlet />
          <SupportWidget key={userStore.user?.shopDomain} shopDomain={userStore.user?.shopDomain ?? ''} defaultEmail={userStore.user?.email} />
        </AppFrame>
      </Fragment>
    </LegacyModeProvider>
  )

};

export default observer(App);
